import { ArrowUpRight, Check, Minus } from "lucide-react";
import Reveal, { Eyebrow } from "@/components/Reveal";
import Newsletter from "@/components/Newsletter";
import { STAYS, LINKS, IMAGES } from "@/data/content";

const VS = [
  { label: "Included hot breakfast", drury: true, hyatt: true },
  { label: "Evening drinks & snacks (5:30 Kickback)", drury: true, hyatt: false },
  { label: "Suite-style layouts for families", drury: false, hyatt: true },
  { label: "Consistent value pricing near the parks", drury: true, hyatt: true },
  { label: "Best for", drury: "Value-focused families", hyatt: "Space-first groups" },
];

export default function Stays() {
  return (
    <div data-testid="stays-page">
      <section className="relative grain-overlay overflow-hidden">
        <div className="absolute top-0 right-1/4 h-[380px] w-[380px] rounded-full bg-ocean/10 blur-3xl" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-36 sm:pt-44 pb-16 sm:pb-20">
          <Reveal>
            <Eyebrow>Stays</Eyebrow>
            <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl text-navy tracking-tight max-w-3xl leading-[1.08]">
              Hotels, compared <span className="italic text-ocean">honestly.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-charcoal/75 max-w-2xl leading-relaxed">
              Roundups and head-to-head comparisons organized by location, traveler type, amenities, and value —
              not just star rating.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {STAYS.map((s, i) => (
            <Reveal key={s.slug} delay={i * 0.08}>
              <article
                data-testid={`stays-page-card-${s.slug}`}
                className="group h-full rounded-3xl bg-white border border-slateblue/15 overflow-hidden transition-[transform,box-shadow,border-color] duration-500 hover:-translate-y-1.5 hover:shadow-[0_24px_48px_-16px_rgba(16,42,67,0.18)] hover:border-gold/40"
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={s.image}
                    alt={s.title}
                    className="h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
                  />
                  <span className="absolute top-4 left-4 rounded-full bg-ivory/90 backdrop-blur px-3.5 py-1.5 text-[10px] font-semibold tracking-[0.16em] uppercase text-navy">
                    {s.category}
                  </span>
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-slateblue">{s.location}</p>
                    <p className="text-xs font-semibold text-gold whitespace-nowrap">{s.price}</p>
                  </div>
                  <h2 className="mt-3 font-serif text-xl text-navy leading-snug">{s.title}</h2>
                  <p className="mt-3 text-sm text-charcoal/70 leading-relaxed">{s.why}</p>
                  <a
                    href={s.booking}
                    target="_blank"
                    rel="noopener noreferrer sponsored"
                    data-testid={`stays-page-booking-${s.slug}`}
                    className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold tracking-[0.14em] uppercase text-ocean hover:text-gold transition-colors duration-300"
                  >
                    Check current availability
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </a>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 sm:pb-28">
        <div className="grid lg:grid-cols-2 gap-10 items-stretch">
          <Reveal>
            <div className="relative h-full min-h-[320px] rounded-3xl overflow-hidden border-4 border-white shadow-[0_32px_64px_-24px_rgba(16,42,67,0.3)]">
              <img
                src={IMAGES.resortPalms}
                alt="A resort pool framed by palms in Orlando"
                className="absolute inset-0 h-full w-full object-cover"
                data-testid="comparison-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/70 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold">Head-to-head</p>
                <p className="mt-2 font-serif text-2xl sm:text-3xl text-ivory leading-snug">
                  Drury Plaza vs. Hyatt Place:<br />which is better for your Orlando stay?
                </p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="h-full rounded-3xl bg-white border border-slateblue/15 p-7 sm:p-9" data-testid="comparison-table">
              <div className="grid grid-cols-[1fr_auto_auto] gap-x-4 items-center pb-4 border-b border-slateblue/15">
                <span className="text-xs tracking-[0.2em] uppercase text-slateblue font-semibold">What matters</span>
                <span className="font-serif text-lg text-navy text-center w-20">Drury</span>
                <span className="font-serif text-lg text-navy text-center w-20">Hyatt</span>
              </div>
              {VS.map((row) => (
                <div
                  key={row.label}
                  className="grid grid-cols-[1fr_auto_auto] gap-x-4 items-center py-3.5 border-b border-slateblue/10 last:border-0"
                >
                  <span className="text-sm text-charcoal/80">{row.label}</span>
                  {typeof row.drury === "boolean" ? (
                    <>
                      <span className="w-20 flex justify-center">
                        {row.drury ? <Check className="h-4 w-4 text-gold" /> : <Minus className="h-4 w-4 text-slateblue/50" />}
                      </span>
                      <span className="w-20 flex justify-center">
                        {row.hyatt ? <Check className="h-4 w-4 text-gold" /> : <Minus className="h-4 w-4 text-slateblue/50" />}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="w-20 text-center text-xs font-semibold text-ocean">{row.drury}</span>
                      <span className="w-20 text-center text-xs font-semibold text-ocean">{row.hyatt}</span>
                    </>
                  )}
                </div>
              ))}
              <a
                href={LINKS.bookingOrlando}
                target="_blank"
                rel="noopener noreferrer sponsored"
                data-testid="comparison-booking-button"
                className="mt-7 inline-flex items-center gap-2 rounded-full bg-navy px-7 py-3.5 text-sm font-semibold text-ivory transition-colors duration-300 hover:bg-ocean"
              >
                Browse family-friendly stays
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <p className="mt-10 text-xs text-slateblue leading-relaxed max-w-2xl" data-testid="stays-disclosure-note">
            Availability buttons link to our hotel booking partners. If you book through them, we may earn a
            commission at no additional cost to you.
          </p>
        </Reveal>
      </section>
      <Newsletter />
    </div>
  );
}
